import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Grid from "@material-ui/core/Grid";
import Checkbox from "@material-ui/core/Checkbox";
import Check from "@material-ui/icons/Check";
import ActionButtons from "./ActionButtons";
import allStyles from "assets/jss/elite-feats-cms/views/extendedTablesStyle";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles({
  ...allStyles,
  card: {
    marginBottom: 15,
    position: "relative",
    border: "1px solid #e0e0e0",
    boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.14)",
  },
  cardContent: {
    paddingTop: 20,
    paddingBottom: 10,
  },
  cardActions: {
    padding: "8px 16px",
    justifyContent: "center",
    borderTop: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
  },
  checkboxContainer: {
    position: "absolute",
    top: 8,
    right: 8,
  },
  name: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 8,
    paddingRight: 40, // Make room for checkbox
  },
  email: {
    fontSize: 14,
    color: "#666",
    marginBottom: 12,
  },
  groupsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 8,
  },
  groupChip: {
    fontSize: 12,
    height: 24,
    backgroundColor: "#40628d",
    color: "#f7f7f7",
  },
  noGroups: {
    fontSize: 14,
    fontWeight: 600,
    color: "#000",
  },
});

/**
 * ClientCard Component
 * Card-based display for a single client in mobile view
 *
 * @param {Object} client - Full client object
 * @param {number} index - Card index for tour classes
 * @param {Function} onToggle - Checkbox toggle callback
 * @param {Function} onEdit - Edit button callback
 * @param {Function} onAssessment - Assessment button callback
 * @param {Function} onEmail - Email button callback
 * @param {boolean} isChecked - Checkbox checked state
 */
interface ClientCardProps {
  client: Record<string, unknown>;
  index: number;
  onToggle: (email: string) => void;
  onEdit: () => void;
  onAssessment: () => void;
  onEmail: () => void;
  isChecked?: boolean;
}

export default function ClientCard({
  client,
  index,
  onToggle,
  onEdit,
  onAssessment,
  onEmail,
  isChecked,
}: ClientCardProps): React.ReactElement {
  const classes = useStyles() as Record<string, string>;
  const { t } = useTranslation();

  // Handle checkbox click
  const handleCheckboxClick = () => {
    onToggle(client.email as string);
  };

  // Format full name
  const fullName = `${client.first || client.first_name || ""} ${
    client.last || client.last_name || ""
  }`.trim();

  // Get groups array
  const groups = (
    Array.isArray(client.group)
      ? client.group
      : client.groups
      ? client.groups
      : []
  ) as unknown[];

  return (
    <Card className={classes.card}>
      {/* Checkbox in top-right corner */}
      <div className={`tour-select-${index} ${classes.checkboxContainer}`}>
        <Checkbox
          style={{ padding: 7 }}
          checked={isChecked}
          onChange={handleCheckboxClick}
          checkedIcon={<Check className={classes.checkedIcon} />}
          icon={<Check className={classes.uncheckedIcon} />}
          classes={{
            checked: classes.checkedGreen,
            root: classes.checkGreen,
          }}
        />
      </div>

      <CardContent className={classes.cardContent}>
        {/* Client Name */}
        <Typography className={classes.name}>{fullName}</Typography>

        {/* Email */}
        <Typography className={classes.email}>{client.email}</Typography>

        {/* Groups */}
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {t("clients:headerGroups")}:
            </Typography>
            {groups.length > 0 ? (
              <div className={classes.groupsContainer}>
                {groups.map((group: unknown, idx: number) => (
                  <Chip
                    key={idx}
                    label={group}
                    size="small"
                    className={classes.groupChip}
                  />
                ))}
              </div>
            ) : (
              <Typography className={classes.noGroups}> - </Typography>
            )}
          </Grid>
        </Grid>
      </CardContent>

      {/* Action Buttons */}
      <CardActions className={classes.cardActions}>
        <ActionButtons
          client={(client.full as Record<string, unknown>) || client}
          index={index}
          onEdit={onEdit}
          onAssessment={onAssessment}
          onEmail={onEmail}
          isMobile={true}
          layout="horizontal"
        />
      </CardActions>
    </Card>
  );
}
